import styles from "./page-searchbar.module.css";
import { useTranslations } from "next-intl";
import { MdKeyboardArrowDown } from "react-icons/md";
import MoreFiltersComponent from "../more-filters/more-filters-component";
import { useEffect, useRef, useState } from "react";
import {
  AutocompleteSuggestion,
  OfficeCategoryEnum,
  PurposeEnum,
  Search,
  TypeEnum,
} from "@/types/types";
import { listingsNumberFormatter } from "@/lib/price-formatter";
import { getFilteredListingCountFromAutocomplete } from "@/lib/getFilteredLocationsData/autocomplete_data";
import { CircleFlag } from "react-circle-flags";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import TypeSelectorClient from "./type/type-selector.client";
import PurposeSelectorClient from "./purpose/purpose-selector.client";
import { useDropdownPosition } from "@/hooks/use-dropdown-position";
import CategorySelectorClient from "./category/category-selector.client";
import BuildingTypeSelectorClient from "./building-type/building-type-selector.client";
import OfficeTypeSelectorClient from "./office-type/office-type-selector.client";
import { usePageStore } from "../../usePageStore";
import TypeSelectorServer from "./type/type-selector.server";
import PurposeSelectorServer from "./purpose/purpose-selector.server";
import CategorySelectorServer from "./category/category-selector.server";
import BuildingTypeSelectorServer from "./building-type/building-type-selector.server";
import OfficeTypeSelectorServer from "./office-type/office-type-selector.server";
import { IoSearchOutline } from "react-icons/io5";
import { IoIosArrowDown } from "react-icons/io";

export default function PageSearchbar({
  ssrSearch,
  numberOfActiveFilters,
  locale,
  userLat,
  userLon,
}: {
  ssrSearch: Search;
  numberOfActiveFilters: number;
  locale: string;
  userLat: string | null;
  userLon: string | null;
}) {
  const t = useTranslations("PagesSearchbars");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const numberOfActiveFilter = usePageStore(
    (state) => state.searchFiltersStatus.numberOfActiveFilters,
  );
  const isSearchHydrated = usePageStore((state) => state.isSearchHydrated);
  const type = usePageStore((state) => state.search.type);
  const purpose = usePageStore((state) => state.search.purpose);
  const category = usePageStore((state) => state.search.category);
  const is_luxury = usePageStore((state) => state.search.is_luxury);
  const new_development = usePageStore((state) => state.search.new_development);
  const isSuggestionsOpen = usePageStore((state) => state.isSuggestionsOpen);
  const suggestions = usePageStore((state) => state.suggestions);
  const text = usePageStore((state) => state.text);
  const onTextChange = usePageStore((state) => state.onTextChange);
  const currentSuggestion = usePageStore((state) => state.currentSuggestion);
  const refetchState = usePageStore().refetchState;
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);
  const setNewSuggestion = usePageStore((state) => state.setNewSuggestion);
  const searchNewLocation = usePageStore((state) => state.searchNewLocation);
  const setIsSuggestionsOpen = usePageStore(
    (state) => state.setIsSuggestionsOpen,
  );
  const updateSearch = usePageStore((state) => state.updateSearch);
  const suggestionsWrapperRef = useRef<HTMLDivElement>(null);
  const suggestionListRef = useRef<HTMLUListElement>(null);
  const suggestionsValuesRef = useRef<AutocompleteSuggestion[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const pos = useRef({ left: 0, x: 0, isDown: false });
  const filtersListStyles = useDropdownPosition(
    filterButtonRef,
    isFiltersOpen,
    {
      alignRight: false,
      customWidth: 600, // px
    },
  );
  const suggestionListStyles = useDropdownPosition(
    suggestionsWrapperRef,
    isSuggestionsOpen,
    {
      customWidth: 300, // px
    },
  );

  useEffect(() => {
    suggestionsValuesRef.current = suggestions;
  }, [suggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionListRef.current &&
        !suggestionListRef.current.contains(event.target as Node)
      ) {
        setNewSuggestion(suggestionsValuesRef.current[0], locale);
        setIsSuggestionsOpen(false);
      }
    };
    if (isSuggestionsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSuggestionsOpen]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let isDragging = false;

    const onMouseDown = (e: MouseEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      pos.current = {
        left: el.scrollLeft,
        x: e.clientX,
        isDown: true,
      };
      isDragging = false;
      el.style.cursor = "grabbing";
    };

    const onMouseMove = (e: MouseEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (!pos.current.isDown) return;
      const dx = e.clientX - pos.current.x;
      if (Math.abs(dx) > 3) {
        // small threshold so tiny jitter doesn’t count as drag
        isDragging = true;
      }
      el.scrollLeft = pos.current.left - dx;
    };

    const onMouseUp = () => {
      pos.current.isDown = false;
      updateCursor();
    };

    const onClick = (e: MouseEvent) => {
      if (isDragging) {
        e.stopPropagation();
        e.preventDefault(); // prevent clicks on children if user was dragging
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      pos.current = {
        left: el.scrollLeft,
        x: e.touches[0].clientX,
        isDown: true,
      };
      isDragging = false;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pos.current.isDown) return;
      const dx = e.touches[0].clientX - pos.current.x;
      if (Math.abs(dx) > 3) {
        isDragging = true;
      }
      el.scrollLeft = pos.current.left - dx;
    };

    const onTouchEnd = (e: TouchEvent) => {
      pos.current.isDown = false;
      if (isDragging) {
        e.preventDefault(); // prevent taps if drag happened
      }
    };

    const updateCursor = () => {
      if (!el) return;

      const isScrollable = el.scrollWidth > el.clientWidth;
      el.style.cursor = isScrollable ? "grab" : "default";
    };

    window.addEventListener("resize", updateCursor);
    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("mousemove", onMouseMove);
    el.addEventListener("mouseup", onMouseUp);
    el.addEventListener("mouseleave", onMouseUp);
    el.addEventListener("click", onClick, true); // capture phase, before buttons

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd);

    updateCursor();

    return () => {
      window.removeEventListener("resize", updateCursor);
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("mouseleave", onMouseUp);
      el.removeEventListener("click", onClick, true);

      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return isSearchHydrated ? (
    <form
      className={styles.outer}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div ref={scrollRef} className={styles.inner}>
        <div className={styles.locationWrapper} ref={suggestionsWrapperRef}>
          <div
            className={styles.locationPill}
            onClick={(e) => {
              e.preventDefault();
              if (
                e.target instanceof HTMLElement &&
                (e.target.tagName === "BUTTON" || e.target.closest("button"))
              ) {
                return;
              }

              if (!inputRef.current) return;
              if (!suggestionsWrapperRef.current) return;
              inputRef.current.focus();
              suggestionsWrapperRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest",
              });
            }}
          >
            <input
              className={styles.input}
              ref={inputRef}
              type="text"
              value={text}
              placeholder={t("fieldDescription")}
              onChange={(e) => onTextChange(e, locale, userLat, userLon)}
              onClick={(e) => {
                e.preventDefault();
                if (!inputRef.current) return;
                if (!suggestionsWrapperRef.current) return;
                inputRef.current.focus();
                suggestionsWrapperRef.current.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "nearest",
                });
              }}
            />
            {currentSuggestion !== null &&
              text === currentSuggestion.names[locale] && (
                <span
                  className={`${styles.listingsNumber} ${
                    text !== "" && styles.hasText
                  }`}
                >
                  (
                  {currentSuggestion !== null &&
                    listingsNumberFormatter(
                      getFilteredListingCountFromAutocomplete(
                        currentSuggestion,
                        type,
                        purpose,
                      ),
                    )}
                  )
                </span>
              )}
            <button
              onClick={async (e) => {
                e.preventDefault();
                await searchNewLocation(locale, setCache);
              }}
              className={styles.locationSearch}
            >
              <IoSearchOutline />
            </button>
          </div>
          {isSuggestionsOpen && (
            <ul
              className={styles.suggestionsList}
              ref={suggestionListRef}
              style={suggestionListStyles}
            >
              {suggestions.map((suggestion, index) => {
                return (
                  <li key={index}>
                    <button
                      className={`${styles.listButton} ${
                        currentSuggestion?.slug === suggestion.slug &&
                        styles.isActive
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        setNewSuggestion(suggestion, locale);
                      }}
                    >
                      <span className={styles.flagAndNameWrapper}>
                        {suggestion.country_code && (
                          <CircleFlag
                            className={styles.countryFlag}
                            countryCode={suggestion.country_code?.toLowerCase()}
                          />
                        )}
                        <span className={styles.name}>
                          {suggestion.names[locale] ?? suggestion.names["en"]}
                        </span>
                      </span>
                      <span className={styles.number}>
                        (
                        {listingsNumberFormatter(
                          getFilteredListingCountFromAutocomplete(
                            suggestion,
                            type,
                            purpose,
                          ),
                        )}
                        )
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className={styles.separator} />
        <div className={styles.selectWrapper}>
          <TypeSelectorClient
            t={t}
            type={type}
            refetchState={async () => {
              await refetchState(locale, setCache, true);
            }}
          />
        </div>
        <div className={styles.separator} />
        <div className={styles.selectWrapper}>
          <PurposeSelectorClient
            t={t}
            purpose={purpose}
            refetchState={async () => {
              await refetchState(locale, setCache, true);
            }}
          />
        </div>
        {type !== TypeEnum.APARTMENT_HOUSE && (
          <>
            <div className={styles.separator} />
            <div className={styles.selectWrapper}>
              <CategorySelectorClient
                t={t}
                type={type}
                activeCategory={category}
                updateSearch={updateSearch}
                refetchState={async () => {
                  await refetchState(locale, setCache, true);
                }}
              />
            </div>
          </>
        )}
        {type === TypeEnum.APARTMENT && (
          <>
            <div className={styles.separator} />
            <div className={styles.selectWrapper}>
              <BuildingTypeSelectorClient
                t={t}
                updateSearch={updateSearch}
                refetchState={async () => {
                  await refetchState(locale, setCache, true);
                }}
              />
            </div>
          </>
        )}
        {type === TypeEnum.OFFICE && category === OfficeCategoryEnum.OFFICE && (
          <>
            <div className={styles.separator} />
            <div className={styles.selectWrapper}>
              <OfficeTypeSelectorClient
                t={t}
                updateSearch={updateSearch}
                refetchState={async () => {
                  await refetchState(locale, setCache, true);
                }}
              />
            </div>
          </>
        )}
        {type !== TypeEnum.LAND && (
          <>
            <div className={styles.separator} />
            <div
              className={`${styles.selectWrapper} ${styles.isLuxury} ${
                is_luxury && styles.isSelected
              }`}
            >
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  updateSearch("is_luxury", !is_luxury);
                  await refetchState(locale, setCache, true);
                }}
                className={`${styles.pill} ${styles.togglePill} ${
                  is_luxury && styles.pillActive
                }`}
              >
                <span>{t("isLuxury")}</span>
              </button>
            </div>
          </>
        )}
        {type !== TypeEnum.LAND && (
          <>
            <div className={styles.separator} />
            <div
              className={`${styles.selectWrapper} ${styles.newDevelopment} ${
                new_development && styles.isSelected
              }`}
            >
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  updateSearch("new_development", !new_development);
                  await refetchState(locale, setCache, true);
                }}
                className={`${styles.pill} ${styles.togglePill} ${
                  new_development && styles.pillActive
                }`}
              >
                <span>{t("isNewDevelopment")}</span>
              </button>
            </div>
          </>
        )}
        <div className={styles.separator} />
        <div className={styles.selectWrapper}>
          <button
            onClick={(e) => {
              e.preventDefault();
              const validateSeachFilters =
                usePageStore.getState().validateSeachFilters;
              const isNotValid = validateSeachFilters();
              if (isNotValid && isFiltersOpen) {
                return;
              }
              setIsFiltersOpen(!isFiltersOpen);
            }}
            className={`${styles.pill} ${isFiltersOpen && styles.pillOpen}`}
            ref={filterButtonRef}
          >
            <div className={styles.textWrapper}>
              <span>{t("moreFilters")}</span>
            </div>
            <span className={styles.chevron}>
              <IoIosArrowDown />
            </span>
            {numberOfActiveFilter > 0 && (
              <span className={styles.activeFilters}>
                <span>{numberOfActiveFilter}</span>
              </span>
            )}
          </button>
          {isFiltersOpen && filtersListStyles.top && (
            <div
              className={styles.moreFiltersWrapper}
              style={filtersListStyles}
            >
              <MoreFiltersComponent
                onClose={() => {
                  setIsFiltersOpen(false);
                }}
                parentRef={filterButtonRef}
              />
            </div>
          )}
        </div>
      </div>
    </form>
  ) : (
    <form className={styles.outer}>
      <div ref={scrollRef} className={styles.inner}>
        <div>
          <div className={`${styles.locationPill}`}>
            <input
              className={styles.input}
              type="text"
              value={ssrSearch.location_name ?? ""}
              placeholder={t("fieldDescription")}
              readOnly
            />
            {ssrSearch.listing_count !== null &&
              ssrSearch.listing_count >= 0 &&
              ssrSearch.location_name && (
                <span
                  className={`${styles.listingsNumber} ${
                    ssrSearch.location_name !== "" && styles.hasText
                  }`}
                >
                  ({ssrSearch.listing_count})
                </span>
              )}
            <button className={styles.locationSearch}>
              <IoSearchOutline />
            </button>
          </div>
        </div>
        <div className={styles.separator} />
        <div className={styles.selectWrapper}>
          <TypeSelectorServer t={t} type={ssrSearch.type} />
        </div>
        <div className={styles.separator} />
        <PurposeSelectorServer t={t} purpose={ssrSearch.purpose} />
        {ssrSearch.type !== TypeEnum.APARTMENT_HOUSE && (
          <>
            <div className={styles.separator} />
            <div className={styles.selectWrapper}>
              <CategorySelectorServer
                t={t}
                activeCategory={ssrSearch.category}
              />
            </div>
          </>
        )}
        {ssrSearch.type === TypeEnum.APARTMENT && (
          <>
            <div className={styles.separator} />
            <div className={styles.selectWrapper}>
              <BuildingTypeSelectorServer
                t={t}
                activeBuildingType={ssrSearch.building_type}
              />
            </div>
          </>
        )}
        {ssrSearch.type === TypeEnum.OFFICE &&
          ssrSearch.category === OfficeCategoryEnum.OFFICE && (
            <>
              <div className={styles.separator} />
              <div className={styles.selectWrapper}>
                <OfficeTypeSelectorServer
                  t={t}
                  activeOfficeType={ssrSearch.office_type}
                />
              </div>
            </>
          )}
        {ssrSearch.type !== TypeEnum.LAND && (
          <>
            <div className={styles.separator} />
            <div className={`${styles.selectWrapper}`}>
              <button
                className={`${styles.pill} ${styles.togglePill} ${
                  ssrSearch.is_luxury && styles.pillActive
                }`}
              >
                <div className={styles.textWrapper}>
                  <span>{t("isLuxury")}</span>
                </div>
              </button>
            </div>
          </>
        )}
        {ssrSearch.type !== TypeEnum.LAND && (
          <>
            <div className={styles.separator} />
            <div className={`${styles.selectWrapper}`}>
              <button
                className={`${styles.pill} ${styles.togglePill} ${
                  ssrSearch.new_development && styles.pillActive
                }`}
              >
                <div className={styles.textWrapper}>
                  <span>{t("isNewDevelopment")}</span>
                </div>
              </button>
            </div>
          </>
        )}
        <div className={styles.separator} />
        <div className={`${styles.selectWrapper} ${styles.moreFiltersWrapper}`}>
          <button className={`${styles.pill} ${styles.moreFiltersItem}`}>
            <div className={styles.textWrapper}>
              <span>{t("moreFilters")}</span>
            </div>
            <span className={styles.chevron}>
              <IoIosArrowDown />
            </span>
            {numberOfActiveFilters > 0 && (
              <span className={styles.activeFilters}>
                <span>{numberOfActiveFilters}</span>
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}
